/**
 * @file PlatformUsersPage.tsx
 * @description Управление пользователями платформой (phase08.5 shadcn;
 * phase19.29 — заголовок и главное действие в верхней панели через
 * `CabinetPageHeader`; phase19.28 — список магазинов для ролей из
 * `GET /admin/platform/shops` вместо зашитого «этапа 1»).
 */

import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { UserCreateModal } from "../../components/UserCreateModal";
import { UserMobileCard } from "../../components/UserMobileCard";
import { Alert } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { USER_STATUS_META } from "../../components/platform/status-meta";
import { CabinetPageHeader } from "../../components/ui/CabinetPageHeader";
import { ContextBadge } from "../../components/ui/ContextBadge";
import { DataTable } from "../../components/ui/data-table";
import { EditorialCard } from "../../components/ui/EditorialCard";
import {
  listAdminUsers,
  reactivateAdminUser,
  softDeleteAdminUser,
  type AdminUser,
} from "../../lib/api-admin-users";
import { ApiError } from "../../lib/api-client";
import { UserRowActions } from "./UserRowActions";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { usePlatformShopOptions } from "../../hooks/platform/usePlatformShopOptions";
import { ListError } from "../../components/ui/ListStates";

/** До какой ширины список пользователей — карточками. */
const USERS_CARDS_QUERY = "(max-width: 1279.98px)";

export function PlatformUsersPage() {
  // Карточки вместо таблицы, пока колонки не помещаются без горизонтальной
  // прокрутки: до 1280px (при закреплённой панели на 1024 содержимому
  // остаётся ~720px — шесть колонок и действия не влезают).
  const isMobile = useMediaQuery(USERS_CARDS_QUERY);
  const notify = useNotify();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // phase19.28: магазины — из GET /admin/platform/shops, а не зашитый список.
  const shopOptions = usePlatformShopOptions();
  const shops = shopOptions.shops;

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setUsers(await listAdminUsers());
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось загрузить пользователей",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function replaceUser(updated: AdminUser) {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
  }

  function removeUser(userId: string) {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  }

  async function onToggleActive(u: AdminUser) {
    try {
      const updated = u.isActive
        ? await softDeleteAdminUser(u.id)
        : await reactivateAdminUser(u.id);
      replaceUser(updated);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Операция не удалась");
    }
  }

  const columns: ColumnDef<AdminUser>[] = [
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
    },
    {
      accessorKey: "displayName",
      header: "Имя",
      cell: ({ row }) => row.original.displayName ?? "—",
    },
    {
      id: "status",
      header: "Статус",
      enableSorting: false,
      cell: ({ row }) => {
        // Тон и подпись — из общего реестра статусов платформы.
        const meta = USER_STATUS_META[row.original.isActive ? "active" : "disabled"];
        return <Badge variant={meta.variant}>{meta.label}</Badge>;
      },
    },
    {
      id: "contexts",
      header: "Роли",
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.contexts.length === 0) return <ContextBadge.Empty />;
        return (
          <div className="flex flex-wrap gap-1">
            {row.original.contexts.map((c) => (
              <ContextBadge key={c.id} ctx={c} showScope />
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Создан",
      cell: ({ row }) =>
        row.original.createdAt
          ? new Date(row.original.createdAt).toLocaleDateString("ru-RU")
          : "—",
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Действия</span>,
      enableSorting: false,
      cell: ({ row }) => (
        <UserRowActions
          user={row.original}
          shops={shops}
          onToggle={(u) => void onToggleActive(u)}
          onContextChanged={replaceUser}
          onDeleted={removeUser}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <CabinetPageHeader
        title="Пользователи"
        subtitle="Сотрудники платформы и магазинов: роли, доступ и вход под пользователем"
        action={
          <Button variant="brand" onClick={() => setCreateOpen(true)}>
            <UserPlus className="size-4" />
            Создать пользователя
          </Button>
        }
      />

      {shopOptions.error ? <ListError message={shopOptions.error} onRetry={shopOptions.reload} /> : null}

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : isMobile ? (
        loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="size-6 animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <Alert severity="info">Пользователей нет</Alert>
        ) : (
          <div className="flex flex-col gap-3">
            {users.map((u) => (
              <UserMobileCard
                key={u.id}
                user={u}
                contextsLabel="Роли"
                contextBadges={
                  u.contexts.length === 0 ? (
                    <ContextBadge.Empty />
                  ) : (
                    u.contexts.map((c) => <ContextBadge key={c.id} ctx={c} showScope />)
                  )
                }
                actions={
                  <UserRowActions
                    user={u}
                    shops={shops}
                    onToggle={(uu) => void onToggleActive(uu)}
                    onContextChanged={replaceUser}
                    onDeleted={removeUser}
                  />
                }
              />
            ))}
          </div>
        )
      ) : (
        <EditorialCard padded={false}>
          <EditorialCard.Body className="p-0">
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-primary" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={users}
                empty="Пользователей нет"
                className="p-3"
              />
            )}
          </EditorialCard.Body>
        </EditorialCard>
      )}

      <UserCreateModal
        open={createOpen}
        scope={{ scope: "platform", shops }}
        onClose={() => setCreateOpen(false)}
        onCreated={(u) => setUsers((prev) => [...prev, u])}
      />
    </div>
  );
}
